import { AsyncPipe } from '@angular/common';
import { Component, HostBinding, inject, signal } from '@angular/core';

import { EpcotConfig } from '@frontend/sports/common/client-config-data-access';
import { ThemeBorder, ThemeElevated } from '@frontend/sports/common/ui/shared';
import { SportsThemedCardDirective } from '@frontend/sports/common/ui/sports-themed-card';
import { ComponentLoaderComponent } from '@frontend/sports/deferred/feature/component-loader';
import { NOT_APPLICABLE } from '@frontend/sports/tracking/feature';
import { BannerType, BannerViewModel } from '@frontend/sports/types/components/banners/models';
import { WidgetComponent } from '@frontend/sports/widget/core/feature';
import { DsCard } from '@frontend/ui/card';
import { DeviceService } from '@frontend/vanilla/core';
import { isEmpty } from 'lodash-es';
import { Observable } from 'rxjs';

import { ArcRestrictionService } from '../arc/arc-restriction.service';
import { BannerEsportComponent } from './banner-esport.component';
import { BannerGamesComponent } from './banner-games.component';
import { BannerJackpotComponent } from './banner-jackpot.component';
import { BannerTrackingService } from './banner-tracking.service';
import { ofType } from './banner.model';
import { CasinoBannerLinkComponent } from './casino-banner-link.component';
import { SportBannerLinkComponent } from './sport-banner-link.component';
import { VymlComponent } from './vyml.component';
import { VymlService } from './vyml.service';

@Component({
    selector: 'ms-banner-widget',
    templateUrl: './banner-widget.component.html',
    styleUrl: './banner-widget.component.scss',
    imports: [
        AsyncPipe,
        BannerEsportComponent,
        BannerGamesComponent,
        BannerJackpotComponent,
        CasinoBannerLinkComponent,
        ComponentLoaderComponent,
        SportBannerLinkComponent,
        SportsThemedCardDirective,
        VymlComponent,
        DsCard,
    ],
    providers: [BannerTrackingService, VymlService],
})
export class BannerWidgetComponent extends WidgetComponent<BannerViewModel> {
    @HostBinding('class') className = 'banner-widget';

    @HostBinding('class.esport')
    get esportClass() {
        return this.banner?.type === BannerType.Esport;
    }

    @HostBinding('style.--component-loader-height')
    get bannerComponentSize() {
        if (!this.showLoader) {
            return;
        }

        switch (this.banner?.type) {
            case BannerType.CasinoLink:
                return '70px';
            case BannerType.Games:
            case BannerType.Vyml:
                return '137px';
            case BannerType.Jackpot:
                return '100px';
            case BannerType.SportLink:
                return '76px';
            case BannerType.Esport:
                return '82px';
            default:
                return;
        }
    }

    @HostBinding('style.--component-loader-padding') bannerComponentPadding = '0';

    banner?: BannerViewModel;
    position = NOT_APPLICABLE;
    hideGamingCrossSellByArc$: Observable<boolean>;
    showLoader = true;
    isEpcotEnabled = signal(false);
    isMobile = signal(false);
    themeElevated: ThemeElevated = {
        'sportingbet-br': { isElevated: false },
        'betboo-br': { isElevated: false },
    };
    themeBorder: ThemeBorder = {
        'sportingbet-br': { hasBorder: true },
        'betboo-br': { hasBorder: true },
    };

    private device = inject(DeviceService);
    private arcRestrictionService = inject(ArcRestrictionService);
    private epcotConfig = inject(EpcotConfig);

    constructor() {
        super();
        this.hideGamingCrossSellByArc$ = this.arcRestrictionService.gamingCrossSellEnabled$;
        this.isMobile.update(() => this.device.isMobile);
        this.isEpcotEnabled.update(() => this.epcotConfig.isEnabled);
    }

    protected override onData(data?: BannerViewModel): void {
        if (this.device.isRobot) {
            this.hasData = false;
        } else {
            this.hasData = !isEmpty(data);
            this.banner = data;
        }

        this.setWidgetLoaded();
    }

    bannerDataLoaded(hasBannerData: boolean): void {
        this.hasData = hasBannerData;
        this.showLoader = false;
        this.setWidgetLoaded();
    }

    protected readonly ofType = ofType;
}
